import {
  ConfirmModal,
  Drawer,
  Loading,
  Table,
  TableRowMenuCell,
} from '@/components';
import { useMessage } from '@/providers';
import { TABLE_ROW_MENU_WIDTH } from '@/utils';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import {
  useCreateIndicatorMeasurement,
  useDeleteIndicatorMeasurement,
  useIndicatorMeasurements,
  useUpdateIndicatorMeasurement,
} from '../../hooks';
import { useDrawerState } from '../DrawerStateProvider';
import { IndicatorMeasurementForm } from './IndicatorMeasurementForm';

export const IndicatorMeasurementsPage: FC = () => {
  const { t } = useTranslation();
  const { drawerState, setDrawerState } = useDrawerState();
  const [deletingMeasurement, setDeletingMeasurement] = useState<string | null>(
    null,
  );
  const { openMessage } = useMessage();

  const indicatorMeasurements = useIndicatorMeasurements();
  const { mutateAsync: createIndicatorMeasurementAsync } =
    useCreateIndicatorMeasurement();
  const { mutateAsync: updateIndicatorMeasurementAsync } =
    useUpdateIndicatorMeasurement();
  const { mutateAsync: deleteIndicatorMeasurementAsync } =
    useDeleteIndicatorMeasurement();

  if (indicatorMeasurements.isLoading || !indicatorMeasurements.data) {
    return <Loading />;
  }

  return (
    <>
      <Table
        pagination={false}
        dataSource={indicatorMeasurements.data}
        columns={[
          {
            title: t('name'),
            dataIndex: 'name',
            key: 'name',
          },
          {
            title: '',
            key: 'actions',
            fixed: 'right' as const,
            width: TABLE_ROW_MENU_WIDTH,
            onCell: () => ({
              style: { minWidth: TABLE_ROW_MENU_WIDTH },
            }),
            render: (_, record) => (
              <TableRowMenuCell
                onEdit={() => {
                  setDrawerState({
                    open: true,
                    type: 'indicator-measurement',
                    isEditing: true,
                    id: record.id,
                  });
                }}
                items={[
                  {
                    label: t('delete'),
                    key: 'delete',
                    danger: true,
                    onClick: () => {
                      setDeletingMeasurement(record.id);
                    },
                  },
                ]}
              />
            ),
          },
        ]}
      />

      <Drawer
        destroyOnClose
        open={drawerState.open}
        onClose={() => {
          setDrawerState({ open: false });
        }}
      >
        <IndicatorMeasurementForm
          {...(drawerState.open &&
            drawerState.isEditing && {
              initialValues: {
                name:
                  indicatorMeasurements.data.find(
                    (m) => m.id === drawerState.id,
                  )?.name ?? '',
              },
            })}
          onSubmit={async (data) => {
            invariant(drawerState.open, t('unreachable-code'));
            drawerState.isEditing
              ? await updateIndicatorMeasurementAsync({
                  ...data,
                  id: drawerState.id,
                })
              : await createIndicatorMeasurementAsync(data);

            await Promise.all([
              drawerState.isEditing
                ? openMessage('success', t('message-update-completed'))
                : openMessage('success', t('message-create-completed')),
              (async () => {
                setDrawerState({ open: false });
              })(),
            ]);
          }}
        />
      </Drawer>

      <ConfirmModal
        open={!!deletingMeasurement}
        title={t('delete-confirmation')}
        disclaimerText={t('delete-confirmation-disclaimer')}
        onCancel={() => {
          setDeletingMeasurement(null);
        }}
        onOk={async () => {
          invariant(deletingMeasurement, t('unreachable-code'));
          await deleteIndicatorMeasurementAsync({ id: deletingMeasurement });
          await Promise.all([
            openMessage('success', t('message-delete-completed')),
            (async () => {
              setDeletingMeasurement(null);
            })(),
          ]);
        }}
      />
    </>
  );
};
